/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import Vue from 'vue';
import VueClipboard from 'vue-clipboard2';
import Bootstrap from './bootstrap';
import {initHamburgerMenu} from './initHamburgerMenu';
import {initSidebar} from './initSidebar';
import {initAnimations} from './initAnimations';

import { library } from '@fortawesome/fontawesome-svg-core';
// Be super specific for the icons we are importing from fontawesome so our build times and compiled asset size is minimized.
// Note: You should add the name of the icon to both the import list below AND the "solidIcons" or "brandIcons" array.
// https://fontawesome.com/how-to-use/javascript-api/setup/importing-icons
import { faShieldAlt, faLongArrowAltDown, faLongArrowAltRight, faInfoCircle, faBars, faTimes, faCircleNotch, faCog, faSyncAlt, faChartBar, faSignInAlt, faSignOutAlt, faAngleDoubleRight, faFire, faGamepad, faUserCircle, faQuestionCircle, faLock, faFileAlt, faUserSecret, faHome, faBan, faThumbtack, faBullhorn, faCheck, faShoppingCart, faCreditCard, faTrashAlt, faTimesCircle, faCopy, faMoneyCheck, faFilePdf, faExclamationCircle, faComment, faPlug, faLink, faPaperclip, faUsers, faCommentAlt, faComments, faClock, faCaretDown, faUser, faRandom, faUserPlus, faEdit, faTrash, faSearch, faHashtag, faExchangeAlt, faDollarSign, faTrophy, faPaperPlane, faSpinner, faGift, faHdd, faMicrophone, faMobileAlt, faEuroSign, faRobot, faRedo, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faGuilded, faDiscord, faConnectdevelop , faTwitter, faWindows, faUnity, faCcVisa, faCcAmex, faCcDiscover, faCcJcb, faCcDinersClub, faCcAmazonPay, faCcMastercard, faCcStripe, faCcApplePay } from '@fortawesome/free-brands-svg-icons';
// Gives us the <font-awesome-icon> component instead of adding icons with <i> tags.
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// "FAS" Font Awesome Icons
const solidIcons = [ faShieldAlt, faLongArrowAltDown, faLongArrowAltRight, faInfoCircle, faBars, faTimes, faCircleNotch, faCog, faSyncAlt, faChartBar, faSignInAlt, faSignOutAlt, faAngleDoubleRight, faFire, faGamepad, faUserCircle, faQuestionCircle, faLock, faFileAlt, faUserSecret, faHome, faBan, faThumbtack, faBullhorn, faCheck, faShoppingCart, faCreditCard, faTrashAlt, faTimesCircle, faCopy, faMoneyCheck, faFilePdf, faExclamationCircle, faComment, faPlug, faLink, faPaperclip, faUsers, faCommentAlt, faComments, faClock, faCaretDown, faUser, faRandom, faUserPlus, faEdit, faTrash, faSearch, faHashtag, faExchangeAlt, faDollarSign, faTrophy, faPaperPlane, faSpinner, faGift, faHdd, faMicrophone, faMobileAlt, faEuroSign, faRobot, faRedo, faChevronLeft, faChevronRight ];
// "FAB" Font Awesome Icons
const brandIcons = [ faGuilded, faDiscord, faTwitter, faConnectdevelop, faWindows, faUnity, faCcVisa, faCcAmex, faCcDiscover, faCcJcb, faCcDinersClub, faCcAmazonPay, faCcMastercard, faCcStripe, faCcApplePay ];
library.add(...brandIcons, ...solidIcons);

import PopperTooltip from './components/PopperTooltip.vue';
import SetupInstructions from './components/SetupInstructions.vue';
import CopyToClipboard from './components/CopyToClipboard.vue';
import Trianglify from './components/Triangles.vue';
import ConfirmTooltip from './components/ConfirmTooltip';
import RetryPaymentButton from './components/RetryPaymentButton.vue';
import FormSelect from './components/FormSelect';

window.Vue = Vue;

Vue.use(VueClipboard);

Vue.component('trianglify', Trianglify);
Vue.component('modal', { template: '#modal-template' });
Vue.component('setup-instructions', SetupInstructions);
Vue.component('copy-to-clipboard', CopyToClipboard);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('popper-tooltip', PopperTooltip);
Vue.component('confirm-tooltip', ConfirmTooltip);
Vue.component('retry-payment-button', RetryPaymentButton);

const pageClasses = document.getElementById('app');
if (pageClasses.classList.contains('page-billing')) {
    Vue.component('manage-payment-method', () => import(/* webpackChunkName: "billing" */ './components/billing/ManagePaymentMethod.vue'));
    Vue.component('form-select', FormSelect);
} else if (pageClasses.classList.contains('page-settings')) {
    Vue.component('timezone-selector', () => import(/* webpackChunkName: "settings" */ './components/TimezoneSelector.vue'));
} else if (pageClasses.classList.contains('page-premium')) {
    Vue.component('server-grid', () => import(/* webpackChunkName: "billing" */ './components/billing/ServerGrid.vue'));
    Vue.component('change-currency', () => import(/* webpackChunkName: "billing" */ './components/billing/ChangeCurrency.vue'));
    Vue.component('manage-payment-method', () => import(/* webpackChunkName: "billing" */ './components/billing/ManagePaymentMethod.vue'));
    Vue.component('compare-price', () => import(/* webpackChunkName: "billing" */ './components/billing/ComparePrice.vue'));
} else if (pageClasses.classList.contains('page-premium-processing')) {
    Vue.component('stripe-payment-action', () => import(/* webpackChunkName: "billing" */ './components/billing/StripePaymentAction.vue'));
    Vue.component('progress-tracker', () => import(/* webpackChunkName: "billing" */ './components/billing/ProgressTracker.vue'));
} else if (pageClasses.classList.contains('page-game-tout')) {
    Vue.component('game-interest-tabs', () => import(/* webpackChunkName: "game-tout" */ './components/GameInterestTabs.vue'));
}

const app = new Vue({
    el: '#app',
    name: 'app',
    data: {
        selectedCancelReason: null,
        showModal: false,
        showGameMetadata: false,
    },
    watch: {
        /**
         * Toggle the modalActive class when the modal is activated.
         * @param value
         */
        showModal: function(value) {
            if (!document.body.classList.contains('modalActive') && value == true) {
                document.body.classList.add('modalActive');
            } else {
                document.body.classList.remove('modalActive');
            }
        },
    },
});

// Fire the app actions.
initHamburgerMenu();
initSidebar();
initAnimations();

/**
 * Make sure ajax request don't get captured by our CSRF protection
 */
if (Vue.hasOwnProperty('http')) {
    Vue.http.headers.common['X-CSRF-TOKEN'] = document.head.querySelector('meta[name="csrf-token"]').content;
}

